import { useState } from "react";
import "@/App.css";
import axios from "axios";
import {
  Wrench,
  Download,
  HardDrive,
  GraduationCap,
  Phone,
  Mail,
  MapPin,
  CheckCircle2,
  Clock,
  Shield,
  Monitor,
  Menu,
  X,
  Send,
  ChevronRight } from
"lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  return (
    <header className="glass-header fixed top-0 left-0 right-0 z-50" data-testid="header">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a
            href="#hero"
            onClick={(e) => {e.preventDefault();scrollToSection('hero');}}
            className="flex items-center gap-2"
            data-testid="logo-link">

            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg font-sans">PK</span>
            </div>
            <span className="font-semibold text-xl text-slate-900" style={{ fontFamily: 'Fraunces, serif' }}>
              PK0 Informatique
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            <button
              onClick={() => scrollToSection('services')}
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium"
              data-testid="nav-services">

              Services
            </button>
            <button
              onClick={() => scrollToSection('zone')}
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium"
              data-testid="nav-zone">

              Zone d'intervention
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium"
              data-testid="nav-contact">

              Contact
            </button>
          </nav>

          {/* CTA Button */}
          <a
            href="tel:+33608757865"
            className="hidden md:flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-medium transition-all hover:scale-105 shadow-lg shadow-orange-500/30"
            data-testid="header-phone-cta">

            <Phone className="w-4 h-4" />
            <span>06 08 75 78 65</span>
          </a>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle">

            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen &&
        <div className="md:hidden py-4 border-t border-slate-100" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <button
              onClick={() => scrollToSection('services')}
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium text-left py-2"
              data-testid="mobile-nav-services">

                Services
              </button>
              <button
              onClick={() => scrollToSection('zone')}
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium text-left py-2"
              data-testid="mobile-nav-zone">

                Zone d'intervention
              </button>
              <button
              onClick={() => scrollToSection('contact')}
              className="text-slate-600 hover:text-orange-500 transition-colors font-medium text-left py-2"
              data-testid="mobile-nav-contact">

                Contact
              </button>
              <a
              href="tel:+33608757865"
              className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-full font-medium transition-all mt-2"
              data-testid="mobile-phone-cta">

                <Phone className="w-4 h-4" />
                <span>06 08 75 78 65</span>
              </a>
            </nav>
          </div>
        }
      </div>
    </header>);

};

// Hero Section
const HeroSection = () => {
  return (
    <section id="hero" className="pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden" data-testid="hero-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="animate-slide-in-left">
            <span className="inline-block font-accent text-2xl text-orange-500 mb-4">L'informatique, c'est pas votre truc ?

            </span>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6"
              style={{ fontFamily: 'Fraunces, serif' }}>

              Votre ordinateur est <span className="text-orange-500">fatigué</span> ?
            </h1>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed max-w-xl">Il rame et vous fait perdre patience ? Pas de budget pour un neuf ?

              <strong className="text-slate-800"> Je peux vous aider.</strong> Un diagnostic clair et une solution adaptée à votre budget.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a
                href="tel:+33608757865"
                className="cta-pulse inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-medium text-lg transition-all hover:scale-105 shadow-lg shadow-orange-500/30"
                data-testid="hero-phone-cta">

                <Phone className="w-5 h-5" />
                <span>Appelez-moi</span>
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 bg-white hover:bg-slate-50 text-slate-900 px-8 py-4 rounded-full font-medium text-lg transition-all border-2 border-slate-200 hover:border-orange-300"
                data-testid="hero-contact-cta">

                <Mail className="w-5 h-5" />
                <span>Me contacter</span>
              </a>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4">
              <div className="trust-badge" data-testid="trust-badge-local">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span>Ouest Lyonnais</span>
              </div>
              <div className="trust-badge" data-testid="trust-badge-fast">
                <Clock className="w-4 h-4 text-orange-500" />
                <span>Intervention rapide</span>
              </div>
              <div className="trust-badge" data-testid="trust-badge-trust">
                <Shield className="w-4 h-4 text-orange-500" />
                <span>Service de confiance</span>
              </div>
            </div>
          </div>

          {/* Right Image */}
          <div className="relative animate-slide-in-right animation-delay-200 hidden lg:block">
            <div className="hero-image-wrapper">
              <div className="blob-shape overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1626970851408-f9be7f5ab242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxmcnVzdHJhdGVkJTIwcGVyc29uJTIwd2l0aCUyMHNsb3clMjBjb21wdXRlcnxlbnwwfHx8fDE3NzIzNTQ3NzZ8MA&ixlib=rb-4.1.0&q=85"
                  alt="Personne frustrée devant son ordinateur"
                  className="w-full h-[500px] object-cover"
                  data-testid="hero-image" />

              </div>
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-white p-4 rounded-2xl shadow-xl animate-fade-in animation-delay-400">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Problèmes résolus</p>
                  <p className="font-semibold text-slate-900">+ de 500</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Services Section
const ServicesSection = () => {
  const services = [
  {
    id: 'repair',
    icon: Wrench,
    title: 'Réparation PC & Portable',
    description: 'Diagnostic complet et réparation de votre ordinateur fixe ou portable. Je redonne vie à votre machine !',
    features: ['Nettoyage interne', 'Remplacement composants', 'Optimisation performances']
  },
  {
    id: 'software',
    icon: Download,
    title: 'Installation Logiciels',
    description: 'Installation et configuration de vos logiciels, systèmes d\'exploitation et mises à jour.',
    features: ['Windows / Linux', 'Antivirus', 'Logiciels bureautiques']
  },
  {
    id: 'data',
    icon: HardDrive,
    title: 'Récupération de Données',
    description: 'Vos photos, documents ou fichiers importants ont disparu ? Je fais tout pour les récupérer.',
    features: ['Disque dur défaillant', 'Clé USB endommagée', 'Suppression accidentelle']
  },
  {
    id: 'training',
    icon: GraduationCap,
    title: 'Formation Informatique',
    description: 'Apprenez à utiliser votre ordinateur en toute confiance. Cours adaptés à votre niveau.',
    features: ['Initiation ordinateur', 'Internet & emails', 'Sécurité en ligne']
  }];


  return (
    <section id="services" className="py-20 md:py-32 bg-white" data-testid="services-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <span className="inline-block font-accent text-2xl text-orange-500 mb-4">
            Comment puis-je vous aider ?
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
            style={{ fontFamily: 'Fraunces, serif' }}>

            Mes Services
          </h2>
          <p className="text-lg text-slate-600">
            Des solutions informatiques adaptées à vos besoins, avec un service de proximité et des conseils personnalisés.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {services.map((service, index) =>
          <div
            key={service.id}
            className={`service-card bg-white p-8 rounded-2xl border border-orange-100 hover:border-orange-300 cursor-pointer animate-slide-up animation-delay-${index}00`}
            data-testid={`service-card-${service.id}`}>

              <div className="icon-wrapper service-icon mb-6">
                <service.icon className="w-7 h-7" />
              </div>
              <h3
              className="text-xl font-semibold text-slate-900 mb-3"
              style={{ fontFamily: 'Fraunces, serif' }}>

                {service.title}
              </h3>
              <p className="text-slate-600 mb-5 text-sm leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) =>
              <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                    <ChevronRight className="w-4 h-4 text-orange-500" />
                    <span>{feature}</span>
                  </li>
              )}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>);

};

// Zone d'intervention Section
const ZoneSection = () => {
  return (
    <section
      id="zone"
      className="py-20 md:py-32 relative overflow-hidden"
      data-testid="zone-section">

      {/* Background Image with Overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1720109567135-1034301570e0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxseW9uJTIwZnJhbmNlJTIwY291bnRyeXNpZGUlMjBsYW5kc2NhcGV8ZW58MHx8fHwxNzcyMzU0NzgwfDA&ixlib=rb-4.1.0&q=85)'
        }}>

        <div className="absolute inset-0 bg-slate-900/85"></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-block font-accent text-2xl text-orange-400 mb-4">
            Proche de chez vous
          </span>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6"
            style={{ fontFamily: 'Fraunces, serif' }}>

            Zone d'intervention
          </h2>
          <p className="text-lg text-slate-300 mb-12">Je peux me déplacer dans tout l'
            <strong className="text-white">Ouest Lyonnais</strong> pour vous dépanner à domicile si nécessaire.
Pour un état des lieux rapide, je prends d'abord la main à distance sur votre PC.
          </p>

          {/* Remote Support Info */}
          <a
            href="https://www.teamviewer.com/fr/telecharger/windows/"
            target="_blank"
            rel="noopener noreferrer"
            className="block bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-10 border border-white/20 max-w-2xl mx-auto hover:bg-white/20 hover:border-orange-400 transition-all cursor-pointer group"
            data-testid="teamviewer-download-link">

            <div className="flex items-center justify-center gap-3 text-white">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Monitor className="w-6 h-6" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-lg group-hover:text-orange-400 transition-colors">Prise en main à distance</p>
                <p className="text-slate-300 text-sm">Cliquez ici pour télécharger TeamViewer Quick Support</p>
              </div>
              <Download className="w-5 h-5 text-orange-400 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </a>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="tel:+33608757865"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-medium text-lg transition-all hover:scale-105 shadow-lg shadow-orange-500/30"
              data-testid="zone-phone-cta">

              <Phone className="w-5 h-5" />
              <span>06 08 75 78 65</span>
            </a>
          </div>
        </div>
      </div>
    </section>);

};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      toast.success('Message envoyé avec succès ! Je vous réponds rapidement.');
      setFormData({ name: '', email: '', message: '' });
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer ou m\'appeler directement.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32" data-testid="contact-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left - Contact Info */}
          <div>
            <span className="inline-block font-accent text-2xl text-orange-500 mb-4">
              Besoin d'aide ?
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6"
              style={{ fontFamily: 'Fraunces, serif' }}>

              Contactez-moi
            </h2>
            <p className="text-lg text-slate-600 mb-10">
              Un problème informatique ? Une question ? N'hésitez pas à me contacter. 
              Je vous réponds rapidement pour trouver la meilleure solution.
            </p>

            {/* Contact Details */}
            <div className="space-y-6 mb-10">
              <a
                href="tel:+33608757865"
                className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow phone-link"
                data-testid="contact-phone-link">

                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Téléphone</p>
                  <p className="text-xl font-semibold text-slate-900">06 08 75 78 65</p>
                </div>
              </a>

              <div
                className="flex items-center gap-4 p-4 bg-white rounded-2xl shadow-sm"
                data-testid="contact-location">

                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-orange-500" />
                </div>
                <div>
                  <p className="text-sm text-slate-500">Zone</p>
                  <p className="text-xl font-semibold text-slate-900">Vaugneray & Ouest Lyonnais</p>
                </div>
              </div>
            </div>

            {/* Trust Message */}
            <div className="testimonial-card">
              <p className="text-slate-700 mb-4" style={{ fontFamily: 'Fraunces, serif' }}>
                "Un service de proximité, avec une vraie écoute et des solutions adaptées à votre budget."
              </p>
              <p className="text-sm text-slate-500">— PK0 Informatique</p>
            </div>
          </div>

          {/* Right - Contact Form */}
          <div>
            <div className="contact-form-card">
              <h3
                className="text-2xl font-semibold text-slate-900 mb-6"
                style={{ fontFamily: 'Fraunces, serif' }}>

                Envoyez-moi un message
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                    Votre nom
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Jean Dupont"
                    className="w-full h-12 px-4 rounded-xl border-slate-200 focus:border-orange-500 focus:ring-orange-500"
                    data-testid="contact-input-name" />

                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2">
                    Votre email
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="jean.dupont@email.com"
                    className="w-full h-12 px-4 rounded-xl border-slate-200 focus:border-orange-500 focus:ring-orange-500"
                    data-testid="contact-input-email" />

                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                    Votre message
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder="Décrivez votre problème ou votre demande..."
                    className="w-full px-4 py-3 rounded-xl border-slate-200 focus:border-orange-500 focus:ring-orange-500 resize-none"
                    data-testid="contact-input-message" />

                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting || isSuccess}
                  className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white rounded-full font-medium text-lg transition-all hover:scale-[1.02] shadow-lg shadow-orange-500/30 disabled:opacity-70 disabled:cursor-not-allowed"
                  data-testid="contact-submit-btn">

                  {isSubmitting ?
                  <span className="flex items-center justify-center gap-2">
                      <div className="spinner"></div>
                      Envoi en cours...
                    </span> :
                  isSuccess ?
                  <span className="flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Message envoyé !
                    </span> :

                  <span className="flex items-center justify-center gap-2">
                      <Send className="w-5 h-5" />
                      Envoyer le message
                    </span>
                  }
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

// Footer Component
const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-slate-900 text-white py-12" data-testid="footer">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-3 gap-10 items-center">
          {/* Logo & Description */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                <span className="text-white font-bold text-lg font-sans">PK</span>
              </div>
              <span className="font-semibold text-xl" style={{ fontFamily: 'Fraunces, serif' }}>
                PK0 Informatique
              </span>
            </div>
            <p className="text-slate-400 text-sm">
              Dépannage informatique à domicile et à distance dans l'Ouest Lyonnais. 
              Service de proximité et conseils personnalisés.
            </p>
          </div>

          {/* Quick Contact */}
          <div className="text-center space-y-3">
            <a
              href="tel:+33608757865"
              className="inline-flex items-center gap-2 text-2xl font-semibold text-orange-400 hover:text-orange-300 transition-colors"
              data-testid="footer-phone">

              <Phone className="w-6 h-6" />
              06 08 75 78 65
            </a>
            <a
              href="mailto:contact.pkzero@gmail.com"
              className="flex items-center justify-center gap-2 text-slate-400 hover:text-orange-400 transition-colors"
              data-testid="footer-email">

              <Mail className="w-4 h-4" />
              contact.pkzero@gmail.com
            </a>
            <p className="text-slate-400 text-sm">
              <MapPin className="w-4 h-4 inline mr-1" />
              Vaugneray & Ouest Lyonnais
            </p>
          </div>

          {/* Copyright */}
          <div className="text-center md:text-right">
            <p className="text-slate-400 text-sm">
              © {currentYear} PK0 Informatique
            </p>
            <p className="text-slate-500 text-xs mt-1">
              Tous droits réservés
            </p>
          </div>
        </div>
      </div>
    </footer>);

};

// Main App Component
function App() {
  return (
    <div className="App" data-testid="app-container">
      <Toaster position="top-center" richColors />
      <Header />
      <main>
        <HeroSection />
        <ServicesSection />
        <ZoneSection />
        <ContactSection />
      </main>
      <Footer />
    </div>);

}

export default App;